"use client";

import {
    BudgetBounds,
    clampBudget,
} from "@/common/utilities/money/budgetBounds";
import { asCurrency } from "@/common/utilities/math/asCurrency";
import {
    budgetVerdict,
    budgetZones,
    fewOfficesAtBudget,
    TBudgetVerdict,
    typicalTotals,
} from "@/features/office-price-benchmark/budgetAdvice";
import { OfficePriceBenchmark } from "@/features/office-price-benchmark/officePriceBenchmark";
import React, { FC } from "react";
import {
    budgetAdviceActionStyle,
    budgetAdviceBadgeStarStyle,
    budgetAdviceBadgeStyle,
    budgetAdviceBodyStyle,
    budgetAdviceCardStyle,
    budgetAdviceHeaderStyle,
    budgetAdviceSourceStyle,
    budgetAdviceTitleStyle,
    budgetSliderEndsStyle,
    budgetSliderOnZonesStyle,
    budgetSliderOnZonesUnsetStyle,
    budgetSliderTierNameStyle,
    budgetSliderTierStyle,
    budgetSliderTiersStyle,
    budgetSliderTrackStyle,
    budgetSliderTypicalStyle,
    budgetSliderTypicalSwatchStyle,
    budgetSliderTypicalTextStyle,
    budgetSliderZoneStyle,
    budgetSliderZonesStyle,
} from "./BudgetSlider.css";

interface BudgetAdviceInputs {
    bounds: BudgetBounds;
    budget: number | null;
    currency: string;
    seats: number;
    benchmark: OfficePriceBenchmark;
}

/** Everything both halves show, worked out once. */
const describeBudget = ({
    bounds,
    budget,
    currency,
    seats,
    benchmark,
}: BudgetAdviceInputs) => {
    // Whole amounts with thousands separators: "€1,200", "¥85,000". Cents
    // would be false precision on a typical monthly rent.
    const format = (amount: number) =>
        asCurrency(Math.round(amount), currency).toFormat("$0,0");
    const verdict = budgetVerdict(budget, seats, benchmark);
    const typical = typicalTotals(seats, benchmark);
    const seatsText = `${seats} ${seats === 1 ? "seat" : "seats"}`;
    const where =
        benchmark.level === "COUNTRY"
            ? `across ${benchmark.areaName}`
            : `in ${benchmark.areaName}`;
    return {
        format,
        verdict,
        typical,
        seatsText,
        where,
        suggested: clampBudget(seats * benchmark.p50, bounds),
        advice: adviceFor(verdict, {
            where,
            seats: seatsText,
            fewOffices: fewOfficesAtBudget(budget, seats, benchmark),
        }),
    };
};

interface BudgetSliderProps extends Omit<BudgetAdviceInputs, "benchmark"> {
    /** The range input, as the form renders it without advice. */
    input: React.ReactElement<React.InputHTMLAttributes<HTMLInputElement>>;
    /** Typical prices for this seat count; null shows the plain slider. */
    benchmark: OfficePriceBenchmark | null;
}

/**
 * The maximum-budget slider, with its track split into what a budget there
 * gets the team compared with offices of its size around the location:
 * budget, typical or premium.
 *
 * Without a benchmark (no location, conventional space, too little data, or
 * still loading) it renders the plain slider unchanged.
 */
export const BudgetSlider: FC<BudgetSliderProps> = ({
    input,
    benchmark,
    ...rest
}) => {
    if (!benchmark) return input;
    const { bounds, budget, seats } = rest;
    const { format, typical, seatsText, where, verdict } = describeBudget({
        ...rest,
        benchmark,
    });
    const plainValueText = input.props["aria-valuetext"];
    const zones = budgetZones(bounds, seats, benchmark);

    return (
        <>
            <span className={budgetSliderTypicalStyle}>
                <span className={budgetSliderTypicalSwatchStyle} />
                <span className={budgetSliderTypicalTextStyle}>
                    <span>
                        Typical for {seatsText} {where}
                    </span>
                    <strong>
                        {format(typical.low)} – {format(typical.high)}
                    </strong>
                </span>
            </span>

            <span className={budgetSliderTrackStyle}>
                <span className={budgetSliderZonesStyle} aria-hidden>
                    {zones.map((zone) => (
                        <span
                            key={zone.verdict}
                            className={budgetSliderZoneStyle[zone.verdict]}
                            style={{ width: `${zone.widthPercent}%` }}
                        />
                    ))}
                </span>
                {React.cloneElement(input, {
                    className: [
                        budgetSliderOnZonesStyle,
                        budget === null ? budgetSliderOnZonesUnsetStyle : "",
                    ]
                        .filter(Boolean)
                        .join(" "),
                    "aria-valuetext":
                        verdict === "unset"
                            ? plainValueText
                            : `${plainValueText}, ${tierNames[verdict]}`,
                })}
            </span>

            <span className={budgetSliderTiersStyle} aria-hidden>
                {zones.map((zone) => (
                    <span
                        key={zone.verdict}
                        className={budgetSliderTierStyle[zone.verdict]}
                        style={{ width: `${zone.widthPercent}%` }}
                    >
                        {zone.widthPercent > 0 && (
                            <span
                                className={
                                    budgetSliderTierNameStyle[zone.verdict]
                                }
                            >
                                {tierNames[zone.verdict]}
                            </span>
                        )}
                    </span>
                ))}
            </span>

            <span className={budgetSliderEndsStyle} aria-hidden>
                <span>{format(bounds.min)}</span>
                <span>{format(bounds.max)}</span>
            </span>
        </>
    );
};

interface BudgetAdviceProps extends Omit<BudgetAdviceInputs, "benchmark"> {
    benchmark: OfficePriceBenchmark | null;
    onBudgetChange: (budget: number) => void;
}

/**
 * What the chosen budget gets the team around the location, with a one-click
 * typical budget while none is chosen yet. Kept outside
 * the slider's label, which would otherwise swallow clicks on its button.
 */
export const BudgetAdvice: FC<BudgetAdviceProps> = ({
    benchmark,
    onBudgetChange,
    ...rest
}) => {
    if (!benchmark) return null;
    const { format, verdict, where, suggested, advice } = describeBudget({
        ...rest,
        benchmark,
    });

    return (
        <div className={budgetAdviceCardStyle} aria-live="polite">
            <div className={budgetAdviceHeaderStyle}>
                {verdict !== "unset" && (
                    <span className={budgetAdviceBadgeStyle[verdict]}>
                        {verdict === "premium" && (
                            <span className={budgetAdviceBadgeStarStyle}>
                                ★
                            </span>
                        )}
                        {tierNames[verdict]}
                    </span>
                )}
                <p className={budgetAdviceTitleStyle}>{advice.title}</p>
            </div>
            <p className={budgetAdviceBodyStyle}>{advice.body}</p>
            {verdict === "unset" && (
                <button
                    type="button"
                    className={budgetAdviceActionStyle}
                    onClick={() => onBudgetChange(suggested)}
                >
                    Use a typical budget · {format(suggested)}
                </button>
            )}
            <p className={budgetAdviceSourceStyle}>
                Based on asking prices at {benchmark.locations} locations{" "}
                {where}
                {benchmark.allSizes ? ", offices of all sizes" : ""}.
                {benchmark.level === "COUNTRY"
                    ? " Prices vary a lot within a country, so treat this as a rough guide."
                    : ""}
            </p>
        </div>
    );
};

const tierNames: Record<Exclude<TBudgetVerdict, "unset">, string> = {
    budget: "Budget",
    typical: "Typical",
    premium: "Premium",
};

interface AdviceFacts {
    where: string;
    seats: string;
    /** Whether the budget is under where most offices start listing. */
    fewOffices: boolean;
}

/**
 * The card's copy. The badge names the tier, so the title says what the
 * budget gets and the body adds what to expect. Every tier is put as a fair
 * choice: a budget-tier budget is not "too low", a premium one not "too much".
 */
const adviceFor = (
    verdict: TBudgetVerdict,
    f: AdviceFacts
): { title: string; body: string } => {
    switch (verdict) {
        case "unset":
            return {
                title: "How far will your budget go?",
                body: `Pick a budget and we'll compare it with what ${f.seats} usually cost ${f.where}.`,
            };
        case "budget":
            return {
                title: "Smaller or simpler offices",
                body:
                    `Fits the more affordable offices ${f.where}. ` +
                    `Hosts sometimes have deals, so it's worth asking.` +
                    (f.fewOffices ? " Few offices list at this price." : ""),
            };
        case "typical":
            return {
                title: "Most offices within reach",
                body: `In line with what ${f.seats} usually cost ${f.where}.`,
            };
        case "premium":
            return {
                title: "High-end buildings within reach",
                body:
                    `Expect staffed reception, hospitality and prime locations. ` +
                    `Hosts see this as your maximum, not your offer.`,
            };
    }
};
